import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injector, Service } from '@angular/core';
import { AuthStatusMessage } from '@portal-core/auth/enums/auth-status-message.enum';
import { OnAuthError } from '@portal-core/auth/interfaces/auth-error.interface';
import { AuthModuleConfig } from '@portal-core/auth/models/auth-module-config.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthModuleConfigService } from '@portal-core/auth/services/auth-module-config.service';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { AuthInterceptorHttpParams } from '@portal-core/auth/util/auth-interceptor-http-params';
import { Observable, catchError, switchMap, throwError } from 'rxjs';

@Service()
export class AuthInterceptorService implements HttpInterceptor {
  private readonly config = inject<AuthModuleConfig>(AuthModuleConfigService);
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    request = request.clone({
      withCredentials: true
    });

    return next.handle(request).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          // The authorization server answers on the portal host, where the session cookies never
          // arrive: login sets them on the API host and the __Host- prefix forbids a Domain, so the
          // browser cannot send them across. Relocating establishes a session on this origin.
          // Refreshing would not help - that renews the API host's session, not this one.
          if (err.status === 401 && this.isAuthServerRequest(request)) {
            return this.authService.relocateSessionToAuthServer$().pipe(
              switchMap(() => next.handle(request.clone())),
              // The relocation needs a session of its own to mint from, so a signed-out user lands
              // here. Surface the original 401 rather than the relocation's failure.
              catchError(_ => throwError(() => err))
            );
          }

          if (err.status === 401 && !this.ignoreRefreshToken(request)) {
            return this.authService.refreshToken$().pipe(
              switchMap(() => {
                return next.handle(request.clone()); // retry the request
              }),
              catchError(() => {
                // if refresh token fails and we aren't ignoring errors, logout
                if (!this.ignoreError(request)) {
                  this.authService.logout$();
                }
                return throwError(() => err); // throw original 401 error
              })
            );
          } else if (err.status === 403 && !this.ignoreError(request)) {
            const authStatus: AuthStatusMessage = AuthStatusMessage[err.headers.get('X-AuthStatus') as keyof typeof AuthStatusMessage] as AuthStatusMessage;

            // If there is an auth status message AND it is not the NeedsPermission message then this is a drastic auth error
            if (authStatus) {
              if (authStatus !== AuthStatusMessage.NeedsPermission) {
                this.onAuthError(authStatus, err, request);
              }
            } else {
              this.onAuthError(AuthStatusMessage.UserAccessDenied, err, request);
            }
          }
        }

        return throwError(() => err);
      })
    )
  }

  private onAuthError(authStatus: AuthStatusMessage, error: HttpErrorResponse, request: HttpRequest<unknown>) {
    // If any onAuthError event handlers have been registered
    if (Array.isArray(this.config.onAuthError)) {
      // Then loop through each handler and call its method
      this.config.onAuthError.forEach(handlerClass => {
        const handler: OnAuthError = this.injector.get<OnAuthError>(handlerClass);
        if (handler.onAuthError) {
          handler.onAuthError(authStatus, error, request);
        }
      });
    }
  }

  /**
   * Whether a request is bound for the authorization server on this origin.
   *
   * The redeem endpoint is excluded: it is what a relocation calls, so retrying it through a
   * relocation would recurse.
   */
  private isAuthServerRequest(request: HttpRequest<unknown>): boolean {
    const base = this.apiService.centralAuthBaseUri;
    const path = request.url.startsWith(base) ? request.url.substring(base.length) : request.url;

    return path.startsWith('/connect/') && !path.startsWith('/connect/session');
  }

  private ignoreError(request: HttpRequest<unknown>): boolean {
    if (request.params instanceof AuthInterceptorHttpParams) {
      return request.params.interceptorConfig.ignoreAuthErrors;
    } else {
      return false;
    }
  }

  private ignoreRefreshToken(request: HttpRequest<unknown>): boolean {
    if (request.params instanceof AuthInterceptorHttpParams) {
      return request.params.interceptorConfig.ignoreRefreshToken;
    } else {
      return false;
    }
  }
}
